import React from "react";
import {
  Box,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Typography,
  Divider,
} from "@mui/material";
import type { BarFocusSelection } from "@store/slices/chart-config-slice";

interface FocusModeSectionProps {
  focusSelection: BarFocusSelection | null;
  allVariables: any[];
  allValues: any[];
  isDisabled: boolean;
  onFocusSelectionChange: (selection: BarFocusSelection) => void;
  onFocusParamsChange: (params: {
    focusMode: string;
    focusEntity: string;
    focusEntityDisplayLabel: string;
  }) => void;
}

const FocusModeSection: React.FC<FocusModeSectionProps> = ({
  focusSelection,
  allVariables,
  allValues,
  isDisabled,
  onFocusSelectionChange,
  onFocusParamsChange,
}) => {
  return (
    <>
      <Box>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>
          Focus Mode
        </Typography>

        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <FormControl fullWidth size="small">
            <FormLabel className="text-[12px] text-[#262626]">
              Focus Level
            </FormLabel>
            <Select
              value={focusSelection?.mode || "variable"}
              onChange={(e) => {
                const mode = e.target.value as "variable" | "value";
                onFocusSelectionChange({
                  mode,
                  selectedVariableId: focusSelection?.selectedVariableId,
                  selectedValueId: focusSelection?.selectedValueId,
                });
              }}
              disabled={isDisabled}
            >
              <MenuItem value="variable">Variable Level</MenuItem>
              <MenuItem value="value">Value Level</MenuItem>
            </Select>
          </FormControl>

          {focusSelection?.mode === "variable" && (
            <FormControl fullWidth size="small">
              <FormLabel className="text-[12px] text-[#262626]">
                Select Variable
              </FormLabel>
              <Select
                value={focusSelection?.selectedVariableId || ""}
                onChange={(e) => {
                  const selectedVariable = allVariables.find(
                    (v: any) => v.id === e.target.value,
                  );
                  onFocusSelectionChange({
                    mode: "variable",
                    selectedVariableId: e.target.value,
                  });
                  if (selectedVariable) {
                    const displayLabel =
                      selectedVariable.title || selectedVariable.rowtitle;
                    onFocusParamsChange({
                      focusMode: "VARIABLE",
                      focusEntity: selectedVariable.label,
                      focusEntityDisplayLabel: displayLabel,
                    });
                  }
                }}
                disabled={isDisabled}
              >
                {allVariables.length > 0 ? (
                  allVariables.map((v: any) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.rowtitle || v.title}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="">No variables available</MenuItem>
                )}
              </Select>
            </FormControl>
          )}

          {focusSelection?.mode === "value" && (
            <FormControl fullWidth size="small">
              <FormLabel className="text-[12px] text-[#262626]">
                Select Value
              </FormLabel>
              <Select
                value={focusSelection?.selectedValueId || ""}
                onChange={(e) => {
                  const selectedValue = allValues.find(
                    (v: any) => v.id === e.target.value,
                  );
                  onFocusSelectionChange({
                    mode: "value",
                    selectedValueId: e.target.value,
                  });
                  if (selectedValue) {
                    const displayLabel =
                      selectedValue.title || selectedValue.label;
                    onFocusParamsChange({
                      focusMode: "VALUE",
                      focusEntity: String(selectedValue.value),
                      focusEntityDisplayLabel: displayLabel,
                    });
                  }
                }}
                disabled={isDisabled}
              >
                {allValues.length > 0 ? (
                  allValues.map((v: any) => (
                    <MenuItem key={v.id} value={v.id}>
                      {v.title || v.label}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem value="">No values available</MenuItem>
                )}
              </Select>
            </FormControl>
          )}
        </Box>
      </Box>

      <Divider />
    </>
  );
};

export default FocusModeSection;
